import { PINNED_SECTION, SECTION_TYPES, SECTION_VOCABULARY, isSectionType } from '@yourstore/theme';
import type { SlotSpec } from '@yourstore/theme';
import type { ContractSection, ThemeContract } from '../../../wire';

/**
 * **What the builder knows about each kind of section** — one table, built
 * from two sources that do not disagree about anything they both say.
 *
 *  - The **served contract** (`GET …/branding`, `contract.sections`) is the
 *    server's word for the blocks an operator may fill in, and wins wherever
 *    it speaks: the labels, the pages, the slot shapes it will validate.
 *  - The **vocabulary** (`SECTION_VOCABULARY` in `@yourstore/theme`, the table
 *    the server's list is filtered from) fills in the rest: the catalog bands
 *    (`menu`, `stay`, `sailings`, `shop`, `timetable`) and the list-shaped ones
 *    whose editor the old form never had (`features`, `testimonials`, `team`,
 *    `showcase`, `pricing`). The write path validates those against the same
 *    vocabulary (`ARRANGEABLE_SECTIONS`), so a builder that edits them is
 *    asking nothing the server has not already agreed to take.
 *
 * The old editor showed only the first list and carried the second invisibly,
 * which is how a pricing band could be on a page and nowhere on the screen.
 */

export interface BlockSpec {
  type: string;
  /** The vocabulary's English name — the fallback when the console has no word of its own. */
  label: string;
  why: string;
  pages: readonly string[];
  slots: readonly SlotSpec[];
  repeatable: boolean;
  /** The vocabulary's `console_editable`: `false` for the catalog bands and the list-shaped ones. */
  editable: boolean;
}

function fromServed(section: ContractSection): BlockSpec {
  const known = isSectionType(section.type) ? SECTION_VOCABULARY[section.type] : undefined;
  return {
    type: section.type,
    label: section.label,
    why: section.why,
    pages: section.pages.length > 0 ? section.pages : (known?.pages ?? []),
    slots: section.slots.map((slot) => {
      const own = known?.slots.find((s) => s.name === slot.name);
      // The served slot's shape, with the vocabulary's per-entry rules
      // (`entry_max`, `entry_pattern`, `min`) that the wire does not carry.
      return {
        ...(own ?? {}),
        name: slot.name,
        kind: slot.kind,
        label: slot.label,
        max: slot.max,
        ...(slot.fallback === undefined ? {} : { fallback: slot.fallback }),
        ...(slot.options === undefined ? {} : { options: slot.options }),
        ...(slot.hint === undefined ? {} : { hint: slot.hint }),
        ...(slot.alias === undefined ? {} : { alias: slot.alias }),
      };
    }),
    repeatable: section.repeatable,
    editable: known?.console_editable ?? true,
  };
}

/** Every block the builder can place or show, served first where the server speaks. */
export function blockSpecs(contract: ThemeContract): Map<string, BlockSpec> {
  const out = new Map<string, BlockSpec>();
  const served = new Map(contract.sections.map((s) => [s.type, s]));
  for (const type of SECTION_TYPES) {
    const own = served.get(type);
    if (own !== undefined) {
      out.set(type, fromServed(own));
      continue;
    }
    const spec = SECTION_VOCABULARY[type];
    out.set(type, {
      type,
      label: spec.label,
      why: spec.why,
      pages: spec.pages,
      slots: spec.slots,
      repeatable: spec.repeatable === true,
      editable: spec.console_editable,
    });
  }
  // A block a newer server serves and this bundle has never heard of.
  for (const section of contract.sections) {
    if (!out.has(section.type)) out.set(section.type, fromServed(section));
  }
  return out;
}

/** The pinned section — the served one when the server names it, the vocabulary's otherwise. */
export function pinnedOf(contract: ThemeContract): string {
  return contract.pinned_section === '' ? PINNED_SECTION : contract.pinned_section;
}

/** The page kind an arrangement is measured against. */
export function kindOf(page: { kind: 'home' } | { kind: 'content' }): 'home' | 'content' {
  return page.kind;
}

/**
 * The blocks a page may take, in the vocabulary's order — `sectionsForPage`,
 * over the merged table so a served block this bundle does not know is
 * offered too. The four page parts (`category_header` …) never list `home` or
 * `content`, so they never appear.
 */
export function offeredOn(specs: Map<string, BlockSpec>, kind: string): BlockSpec[] {
  return [...specs.values()].filter((spec) => spec.pages.includes(kind));
}

/* ------------------------------------------------------------------ */
/* The slots every band has, and the ones the inspector draws apart    */
/* ------------------------------------------------------------------ */

/** The band's own controls (`BAND_SLOTS`): drawn under "Background", not "What it says". */
export const BAND_SLOT_NAMES: readonly string[] = [
  'tone',
  'divider',
  'kicker',
  'backdrop',
  'wash',
  'surface',
];

/* ------------------------------------------------------------------ */
/* Where a section's content comes from                                */
/* ------------------------------------------------------------------ */

/**
 * **The console tab a data-driven section's content lives on.**
 *
 * Plan §3 C, "data-driven, always": a section that shows the business's data
 * binds to it and the inspector never duplicates it — it says where the data
 * is and links there. The keys are the places an operator can actually go:
 * the catalog tab, the Setup tab, the owner's answers (where their hours come
 * in from), and the Website tab's photos.
 */
export type DataSource = 'catalog' | 'hours' | 'calendar' | 'gift' | 'photos';

export const DATA_SOURCES: Partial<Record<string, DataSource>> = {
  services: 'catalog',
  menu: 'catalog',
  stay: 'catalog',
  sailings: 'catalog',
  shop: 'catalog',
  timetable: 'catalog',
  category_header: 'catalog',
  collection: 'catalog',
  item_header: 'catalog',
  related: 'catalog',
  hours: 'hours',
  booking: 'calendar',
  gift: 'gift',
};

/** The console address a source is edited at. */
export function sourceHref(source: DataSource, tenantId: string): string {
  const base = `#/b/${encodeURIComponent(tenantId)}`;
  switch (source) {
    case 'catalog':
    case 'calendar':
      return `${base}/catalog`;
    case 'hours':
      return `${base}/site/answers`;
    case 'gift':
      return `${base}/setup`;
    case 'photos':
      return `${base}/site`;
  }
}

/** The variant slot's views, or none for a block with one look. */
export function viewsOf(spec: BlockSpec | undefined): readonly string[] {
  return spec?.slots.find((slot) => slot.name === 'variant')?.options ?? [];
}

/** The view a section is showing: its own, else the block's first (its default). */
export function viewOf(spec: BlockSpec | undefined, slots: Record<string, unknown>): string {
  const own = slots['variant'];
  if (typeof own === 'string' && own !== '') return own;
  const variant = spec?.slots.find((slot) => slot.name === 'variant');
  return typeof variant?.fallback === 'string' ? variant.fallback : (variant?.options?.[0] ?? '');
}
